import { Formik, FormikValues } from 'formik';
import React, { useContext } from 'react';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { Components } from './styled';
import ProjectTextField from '../../inputs/ProjectTextField';
import { ToastContext } from '../../../providers/ToastProvider/context';
import useModalState from '../../../hooks/useModalState';
import { isProjectError } from '../../../utils/errors';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { Category } from '../../../api/types/contentManagement';
import { MinimalCategorySchema } from '../../../api/contentManagement/parser';
import { CreateCategoryDto } from '../../../api/typeDtos/contentManagement';
import { createCategory, updateCategory } from '../../../api/contentManagement';

const initialValues = {
  name: '',
  isSubcategory: false,
};

const ModalBody = ({
  modalState,
  onSave,
  onCancel,
}: {
  modalState?: Category;
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const categoryInitialValues = {
    name: modalState?.name,
  };

  const _onSubmit = async (values: FormikValues) => {
    await onSave?.({
      ...values,
    });
  };

  return (
    <Formik
      initialValues={modalState ? categoryInitialValues : initialValues}
      validationSchema={MinimalCategorySchema}
      onSubmit={_onSubmit}>
      {({ values, handleChange, errors, touched }) => (
        <Components.Form>
          <Components.ModalBodyContainer>
            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Value</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.name}
                    onChangeText={handleChange}
                    name={'name'}
                    errorMessage={errors.name && touched.name ? errors?.name : undefined}
                    placeholder={'Enter category name'}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>
          </Components.ModalBodyContainer>
          <Components.ModalButtonsContainer>
            <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
          </Components.ModalButtonsContainer>
        </Components.Form>
      )}
    </Formik>
  );
};

const CreateOrEditCategoryModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  refreshCallback,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { category?: Category };
  refreshCallback?: () => Promise<void>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const confirmationModal = useModalState<boolean>();

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _onSave = async (values: FormikValues) => {
    const data: CreateCategoryDto = {
      name: values?.name,
    };

    setIsLoading(true);
    const result =
      modalState && modalState?.category?.id
        ? await updateCategory({ ...data, id: modalState?.category?.id })
        : await createCategory(data);
    setIsLoading(false);

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      modalState && modalState?.category?.id
        ? showSuccessToast('The changes were saved successfully')
        : showSuccessToast('You successfully added a category');
      onModalClose?.(true);
      refreshCallback?.().then();
    }
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={<ModalBody modalState={modalState?.category} onSave={_onSave} onCancel={_onModalClose} />}
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={modalState?.category ? `Edit category` : 'Create new category'}
        titleSuffix={modalState?.category ? `(ID: ${modalState?.category?.id})` : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};

export default CreateOrEditCategoryModal;
